
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../core/services/auth.service';

interface AdminLoginData {
  token: string;
  user: {
    email: string;
    role: string | null;
    hasMerchant: boolean;
    merchantSlug: string | false;
    socialEmailEmpty?: boolean;
    firstName?: string;
  };
}

@Component({
  selector: 'app-impersonate-admin',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './impersonate-admin.component.html',
  styleUrls: ['./impersonate-admin.component.scss'],
})
export class ImpersonateAdminComponent implements OnInit {
  signinForm: FormGroup;
  impersonateForm: FormGroup;
  impersonateToken: string | null = null;
  private adminLoginData: AdminLoginData | null = null;
  busy = false;
  error = '';
  info = '';

  constructor(private fb: FormBuilder, private http: HttpClient, private authService: AuthService) {
    this.signinForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]],
    });

    this.impersonateForm = this.fb.group({
      email: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.impersonateToken = localStorage.getItem('impersonate-token');
  }

  private apiUrl(path: string): string {
    // environment.apiUrl already contains trailing slash in ApiGatewayService usage
    const base = environment.apiUrl.endsWith('/') ? environment.apiUrl : environment.apiUrl + '/';
    return base + path;
  }

  async submitSignin() {
    if (this.signinForm.invalid) return;
    this.error = '';
    this.info = '';
    this.busy = true;
    const { email, password } = this.signinForm.value;

    this.http
      .post(this.apiUrl('auth/signin'), { email, password }).subscribe({
        next: (res: any) => {
          const token = res && res.data && res.data.token;

          localStorage.setItem('impersonate-token', token);

          this.impersonateToken = token;
          this.adminLoginData = res.data;
          this.info = 'Signed in. You can now impersonate a user.';
          this.busy = false;
        }, error: err => {
          this.error = err?.errMsg || err?.message || err?.error?.message || 'Sign in failed';
          this.busy = false;
        }
      });
  }

  async submitImpersonate() {
    if (this.impersonateForm.invalid) return;
    this.error = '';
    this.info = '';
    this.busy = true;
    const { email } = this.impersonateForm.value;
    const token = this.impersonateToken || localStorage.getItem('impersonate-token');
    if (!token) {
      this.error = 'Missing admin token. Please sign in first.';
      this.busy = false;
      return;
    }

    const headers = new HttpHeaders().set('Authorization', 'Bearer ' + token);
    this.http
      .post(this.apiUrl('auth/impersonate'), { email: email.trim().toLowerCase() }, { headers })
      .subscribe({
        next: (res: any) => {

          this.authService.saveLoginInfo(res.data);
          this.info = 'Impersonation successful. Opening dashboard…';
          setTimeout(() => {
            this.info = '';
          }, 3000);
          window.open('/dashboard', '_blank', 'noopener,noreferrer');

          this.busy = false;
        }, error: e => {
          this.error = e?.errMsg || e?.message || 'Impersonation failed';
          this.busy = false;
        }
      });
  }

  goToAdminDashboard() {
    this.error = '';
    this.info = '';
    this.busy = true;

    const token = this.impersonateToken || localStorage.getItem('impersonate-token');
    if (!token) {
      this.error = 'Missing admin token. Please sign in first.';
      this.busy = false;
      return;
    }

    if (this.adminLoginData) {
      this.bootstrapAdminSession(this.adminLoginData);
      return;
    }

    const headers = new HttpHeaders().set('Authorization', 'Bearer ' + token);
    this.http.get(this.apiUrl('users/me'), { headers }).subscribe({
      next: (res: any) => {
        const user = res?.data;
        if (!user) {
          this.error = 'Could not load admin session.';
          this.busy = false;
          return;
        }

        this.bootstrapAdminSession({
          token,
          user: {
            email: user.email,
            role: user.role,
            hasMerchant: user.hasMerchant,
            merchantSlug: user.merchantSlug,
            firstName: user.firstName,
          },
        });
      },
      error: err => {
        this.error = err?.errMsg || err?.message || err?.error?.message || 'Could not load admin session.';
        this.busy = false;
      },
    });
  }

  private bootstrapAdminSession(loginData: AdminLoginData) {
    if (!this.authService.saveLoginInfo(loginData)) {
      this.error = 'Could not open admin dashboard.';
      this.busy = false;
      return;
    }

    this.authService.getCurrentUserDetails().subscribe({
      next: () => {
        window.open('/admin', '_blank', 'noopener,noreferrer');
        this.busy = false;
      },
      error: err => {
        this.error = err?.errMsg || err?.message || 'Could not open admin dashboard.';
        this.busy = false;
      },
    });
  }
}


